// Shopify calculates automatic discounts in an isolated Storefront cart.
// Only /cart.js is read; the customer's Online Store cart is never changed here.
// No access token or manually maintained discount percentage is required.
(function(global){
  'use strict';
  var API = '/api/2026-07/graphql.json';
  var TTL = 30000;
  var MAX_LINES = 50;

  function attributes(properties){
    return Object.keys(properties || {}).sort().filter(function(key){
      return properties[key] !== null && properties[key] !== '';
    }).map(function(key){ return {key: key, value: String(properties[key])}; });
  }
  function signature(line){
    return JSON.stringify([line.merchandiseId, line.sellingPlanId || '',
      (line.attributes || []).map(function(a){ return [a.key, a.value]; }).sort(function(a,b){
        return a[0].localeCompare(b[0]);
      })]);
  }
  function inputLine(item){
    var line = {
      merchandiseId: 'gid://shopify/ProductVariant/' + (item.variant_id || item.id),
      quantity: item.quantity,
      attributes: attributes(item.properties)
    };
    var plan = item.selling_plan || (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.id);
    if(plan) line.sellingPlanId = 'gid://shopify/SellingPlan/' + plan;
    return line;
  }
  function money(value, currency){
    if(!value || value.currencyCode !== currency || !Number.isFinite(Number(value.amount))) throw new Error('Unexpected currency or price');
    return Math.round(Number(value.amount) * 100);
  }
  async function jsonRequest(url, options){
    var controller = new AbortController();
    var timer = setTimeout(function(){ controller.abort(); }, 10000);
    try {
      var response = await fetch(url, Object.assign({}, options, {signal: controller.signal}));
      if(!response.ok) throw new Error('Price request failed');
      return await response.json();
    } finally { clearTimeout(timer); }
  }

  global.BSDiscountPreview = function(config){
    var snapshotPromise = null;
    var snapshotAt = 0;
    var cache = new Map();
    var active = 0;
    var queue = [];
    function enqueue(task){
      return new Promise(function(resolve,reject){
        queue.push({task:task,resolve:resolve,reject:reject});
        drain();
      });
    }
    function drain(){
      while(active < 3 && queue.length){
        var job = queue.shift();
        active++;
        (function(current){
          Promise.resolve().then(current.task).then(current.resolve,current.reject).finally(function(){ active--; drain(); });
        })(job);
      }
    }
    var root = (config.root || '/').replace(/\/?$/, '/');

    function snapshot(){
      if(!snapshotPromise || Date.now() - snapshotAt > TTL){
        snapshotAt = Date.now();
        cache.clear();
        snapshotPromise = jsonRequest(root + 'cart.js', {credentials:'same-origin', cache:'no-store'});
        snapshotPromise.catch(function(){ snapshotPromise = null; });
      }
      return snapshotPromise;
    }
    async function calculate(cart, items){
      // Bundle transformations need their original parent context; don't guess.
      if(cart.items.some(function(item){ return item.parent_relationship || (item.item_components && item.item_components.length); })) throw new Error('Unsupported cart structure');
      var prospective = items.map(inputLine);
      var lines = cart.items.map(inputLine);
      prospective.forEach(function(line){
        var existing = lines.find(function(other){ return signature(other) === signature(line); });
        if(existing) existing.quantity += line.quantity;
        else lines.push(Object.assign({}, line));
      });
      if(lines.length > MAX_LINES) throw new Error('Cart too large for a price preview');
      var codes = (cart.discount_codes || []).filter(function(code){ return code.applicable; }).map(function(code){ return code.code; });
      var query = 'mutation PricePreview($input: CartInput!, $count: Int!) {' +
        ' cartCreate(input: $input) { userErrors { message } cart {' +
        ' discountCodes { code applicable }' +
        ' lines(first: $count) { pageInfo { hasNextPage } nodes {' +
        ' quantity attributes { key value } merchandise { ... on ProductVariant { id } }' +
        ' sellingPlanAllocation { sellingPlan { id } }' +
        ' cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }' +
        ' } } } } }';
      var result = await jsonRequest(API, {
        method:'POST', credentials:'omit', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({query:query, variables:{count:Math.min(MAX_LINES, lines.length + 5), input:{
          lines:lines, buyerIdentity:{countryCode:config.country || 'DK'},
          discountCodes:codes, attributes:attributes(cart.attributes), note:cart.note || ''
        }}})
      });
      var payload = result.data && result.data.cartCreate;
      if(result.errors || !payload || payload.userErrors.length || !payload.cart || payload.cart.lines.pageInfo.hasNextPage) throw new Error('Price preview unavailable');
      if(codes.some(function(code){
        return !payload.cart.discountCodes.some(function(applied){ return applied.applicable && applied.code.toLowerCase() === code.toLowerCase(); });
      })) throw new Error('Discount context differs');
      var returned = payload.cart.lines.nodes;
      var parsed = returned.map(function(node){
        return {
          merchandiseId:node.merchandise.id,
          sellingPlanId:node.sellingPlanAllocation ? node.sellingPlanAllocation.sellingPlan.id : '',
          attributes:node.attributes, quantity:node.quantity,
          original:money(node.cost.subtotalAmount, config.currency),
          final:money(node.cost.totalAmount, config.currency)
        };
      });
      // Reject partial carts (unavailable products, adjusted quantities or bundles).
      if(parsed.length !== lines.length || lines.some(function(line){
        var matches = parsed.filter(function(other){ return signature(other) === signature(line); });
        return matches.length !== 1 || matches[0].quantity !== line.quantity;
      })) throw new Error('Cart contents differ');
      return prospective.map(function(line){
        var match = parsed.find(function(other){ return signature(other) === signature(line); });
        // A mixed-price quantity (e.g. buy X get Y) has no exact single-unit price.
        // Keep the standard PDP price in that case; the cart shows the exact saving.
        if(match.original % match.quantity || match.final % match.quantity) throw new Error('Uneven per-unit allocation');
        return {original:match.original / match.quantity, price:match.final / match.quantity};
      });
    }
    return {
      invalidate:function(){ snapshotPromise = null; cache.clear(); },
      quote:async function(items){
        var cart = await snapshot();
        var key = JSON.stringify(items);
        if(!cache.has(key)) cache.set(key, enqueue(function(){ return calculate(cart, items); }));
        return cache.get(key);
      }
    };
  };
  // Shared by product cards, add-ons and the product form.
  global.BSRenderPrice = function(element, base, before, options){
    if(!element) return '';
    options = options || {};
    var fmt = function(cents){ return new Intl.NumberFormat('da-DK', {style:'currency',currency:options.currency || 'DKK'}).format(cents / 100); };
    var saving = Math.max(0, before - base);
    var percent = before > 0 ? Math.round(saving * 1000 / before) / 10 : 0;
    var savingText = saving ? 'Spar ' + fmt(saving) + ' (' + String(percent).replace('.', ',') + ' %)' : '';
    element.querySelector('[data-current-price]').textContent = (options.prefix || '') + fmt(base) + (options.suffix || '');
    element.querySelector('[data-before-price]').hidden = !saving;
    element.querySelector('[data-before-amount]').textContent = fmt(before);
    element.querySelector('[data-price-saving]').hidden = !saving;
    element.querySelector('[data-price-saving]').textContent = options.compact && saving ? '-' + String(percent).replace('.', ',') + '%' : savingText;
    return savingText;
  };
  if(global.document && document.body && document.body.dataset.priceCurrency){
    global.BSStorePrices = global.BSDiscountPreview({currency:document.body.dataset.priceCurrency,
      country:document.body.dataset.priceCountry,root:document.body.dataset.priceRoot});
  }
})(window);
